import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { icons } from 'lucide-react'
import { Tooltip } from '../Tooltip'
import type { Placement } from '@floating-ui/react'

export type IconName = keyof typeof icons

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName
  /**
   * Visual size of the button. Defaults to `md`.
   */
  size?: 'sm' | 'md'
  /**
   * Pixel size for the Lucide icon. If not provided, derived from `size`.
   */
  iconSize?: number
  /**
   * Tooltip content to display on hover. If provided, the button will be wrapped in a Tooltip.
   */
  tooltip?: ReactNode
  /**
   * Placement of the tooltip. Only used if `tooltip` is provided.
   */
  tooltipPlacement?: Placement
}

const IconButton = ({
  icon,
  size = 'md',
  iconSize,
  type = 'button',
  className = '',
  tooltip,
  tooltipPlacement,
  ...rest
}: IconButtonProps) => {
  const IconComponent = icons[icon]
  const resolvedIconSize = iconSize ?? (size === 'sm' ? 14 : 20)

  const button = (
    <button type={type} className={`mp-agent-icon-button mp-agent-icon-button-${size} ${className}`.trim()} {...rest}>
      {IconComponent && <IconComponent size={resolvedIconSize} />}
    </button>
  )

  if (tooltip) {
    return (
      <Tooltip content={tooltip} placement={tooltipPlacement}>
        {button}
      </Tooltip>
    )
  }

  return button
}

export default IconButton
